import { useState, useEffect, useCallback } from 'react';
import { 
  ActivityData, 
  ActivityMatchNotification, 
  ActivityMatchSuggestion,
  Workout,
  PlanRecalibrationSuggestion
} from '../types/DataTypes';
import { WorkoutMatchingService } from '../services/workoutMatchingService';
import { storage } from '../services/storage';

interface UseActivityMatchingOptions {
  userId: string;
  autoMatchThreshold?: number; // Confidence threshold for auto-matching (default 0.9)
  enableNotifications?: boolean;
}

export const useActivityMatching = (options: UseActivityMatchingOptions) => {
  const { userId, autoMatchThreshold = 0.9, enableNotifications = true } = options;
  
  const [notifications, setNotifications] = useState<ActivityMatchNotification[]>([]);
  const [pendingActivities, setPendingActivities] = useState<ActivityData[]>([]);
  const [recalibrationSuggestions, setRecalibrationSuggestions] = useState<PlanRecalibrationSuggestion[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastExerciseDates, setLastExerciseDates] = useState<Record<string, string>>({});

  // Load pending notifications and activities on mount
  useEffect(() => {
    loadPendingNotifications();
    loadPendingActivities();
  }, [userId]);

  // Process new activities for matching
  const processNewActivity = useCallback(async (
    activity: ActivityData, 
    scheduledWorkouts: Workout[]
  ): Promise<void> => {
    setIsProcessing(true);
    
    try {
      // Analyze activity for potential matches
      const suggestions = WorkoutMatchingService.analyzeActivityForMatches(
        activity, 
        scheduledWorkouts
      );
      
      if (suggestions.length === 0) {
        // No matches found - create standalone activity
        await createStandaloneActivity(activity);
        return;
      }
      
      const bestMatch = suggestions[0];
      
      // Auto-match if confidence is high enough
      if (bestMatch.confidence >= autoMatchThreshold) {
        await executeActivityMatch(bestMatch, false);
        
        // Show success notification but don't require action
        if (enableNotifications) {
          showAutoMatchNotification(activity, bestMatch);
        }
        return;
      }
      
      // Create notification for user decision
      if (enableNotifications) {
        const notification = WorkoutMatchingService.createMatchNotification(
          activity, 
          suggestions
        );
        
        setNotifications(prev => [notification, ...prev]);
        
        // Persist notification for later if user doesn't see it immediately
        await persistNotification(notification);
      }
      
    } catch (error) {
      console.error('Error processing activity for matching:', error);
      // Fallback to standalone activity
      await createStandaloneActivity(activity);
    } finally {
      setIsProcessing(false);
    }
  }, [autoMatchThreshold, enableNotifications]);

  // Execute a match between activity and workout
  const executeActivityMatch = useCallback(async (
    suggestion: ActivityMatchSuggestion,
    manual: boolean = true
  ): Promise<void> => {
    try {
      // Update activity with match information
      const matchedActivity = WorkoutMatchingService.executeMatch(
        suggestion.activity,
        suggestion.workout,
        suggestion.confidence,
        manual
      );
      
      // Update workout completion status
      await markWorkoutComplete(suggestion.workout.id, matchedActivity);
      
      // Remove from pending activities
      setPendingActivities(prev => 
        prev.filter(a => a.id !== suggestion.activity.id)
      );
      
      // Remove related notifications
      setNotifications(prev => 
        prev.filter(n => n.activity.id !== suggestion.activity.id)
      );
      
      // Trigger plan recalibration analysis
      await analyzePlanRecalibration(matchedActivity);
      
    } catch (error) {
      console.error('Error executing activity match:', error);
      throw error;
    }
  }, []);

  // Reject a match and create standalone activity
  const rejectActivityMatch = useCallback(async (activityId: string): Promise<void> => {
    try {
      const activity = pendingActivities.find(a => a.id === activityId);
      if (!activity) return;
      
      await createStandaloneActivity({
        ...activity,
        status: 'standalone',
        manual_match: true // User explicitly chose not to match
      });
      
      // Remove from pending and notifications
      setPendingActivities(prev => prev.filter(a => a.id !== activityId));
      setNotifications(prev => prev.filter(n => n.activity.id !== activityId));
      
      // Analyze for plan recalibration
      await analyzePlanRecalibration(activity);
      
    } catch (error) {
      console.error('Error rejecting activity match:', error);
      throw error;
    }
  }, [pendingActivities]);

  // Dismiss a notification without action
  const dismissNotification = useCallback((notificationId: string): void => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    // Could persist dismissal preference here
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback((): void => {
    setNotifications([]);
  }, []);

  // Get activity summary for a given time period
  const getActivitySummary = useCallback((_days: number = 7) => {
    // This would fetch and analyze recent activities
    // Return metrics like completion rate, pattern changes, etc.
    return {
      totalActivities: 0,
      matchedActivities: 0,
      standaloneActivities: 0,
      averageMatchConfidence: 0,
      needsRecalibration: recalibrationSuggestions.length > 0
    };
  }, [recalibrationSuggestions]);

  // Helper functions (would integrate with your existing API)
  const loadPendingNotifications = async (): Promise<void> => {
    try {
      // Load from storage or API
      const stored = await storage.getItem(`activity_notifications_${userId}`);
      if (stored) {
        const notifications = JSON.parse(stored);
        // Filter out expired notifications
        const active = notifications.filter((n: ActivityMatchNotification) => 
          new Date(n.expires_at) > new Date()
        );
        setNotifications(active);
      }
    } catch (error) {
      console.error('Error loading pending notifications:', error);
    }
  };

  const loadPendingActivities = async (): Promise<void> => {
    try {
      // Load activities that haven't been matched yet
      // This would typically come from your API
      const pending: ActivityData[] = []; // Fetch from API
      setPendingActivities(pending);
    } catch (error) {
      console.error('Error loading pending activities:', error);
    }
  };

  const persistNotification = async (notification: ActivityMatchNotification): Promise<void> => {
    try {
      const stored = await storage.getItem(`activity_notifications_${userId}`) || '[]';
      const notifications = JSON.parse(stored);
      notifications.push(notification);
      await storage.setItem(`activity_notifications_${userId}`, JSON.stringify(notifications));
    } catch (error) {
      console.error('Error persisting notification:', error);
    }
  };

  const showAutoMatchNotification = (activity: ActivityData, match: ActivityMatchSuggestion): void => {
    // Could show a toast notification or similar for auto-matches
    console.log(`Auto-matched ${activity.sport} activity to ${match.workout.name}`);
  };

  const createStandaloneActivity = async (activity: ActivityData): Promise<void> => {
    try {
      // Create new workout card for this activity
      // This would integrate with your workout creation API
      console.log('Creating standalone activity:', activity);
    } catch (error) {
      console.error('Error creating standalone activity:', error);
      throw error;
    }
  };

  const markWorkoutComplete = async (workoutId: string, activityData: ActivityData): Promise<void> => {
    try {
      // Mark the scheduled workout as complete with activity data
      // This would integrate with your workout completion API
      console.log('Marking workout complete:', workoutId, activityData);
    } catch (error) {
      console.error('Error marking workout complete:', error);
      throw error;
    }
  };

  const analyzePlanRecalibration = async (activity: ActivityData): Promise<void> => {
    try {
      // Analyze if plan needs recalibration based on recent activity
      const suggestion = WorkoutMatchingService.analyzePlanRecalibration(activity);
      
      setRecalibrationSuggestions(suggestion ? [suggestion] : []);
    } catch (error) {
      console.error('Error analyzing plan recalibration:', error);
    }
  };

  // Helper function commented out as it's not currently used
  // const updateLastExerciseDate = useCallback((exerciseId: string, _days: number) => {
  //   // Update the last exercise date for the given exercise
  //   setLastExerciseDates(prev => ({
  //     ...prev,
  //     [exerciseId]: new Date().toISOString()
  //   }));
  // }, []);

  return {
    // State
    notifications,
    pendingActivities,
    recalibrationSuggestions,
    isProcessing,
    lastExerciseDates,
    
    // Actions
    processNewActivity,
    executeActivityMatch,
    rejectActivityMatch,
    dismissNotification,
    clearAllNotifications,
    
    // Utilities
    getActivitySummary,
    
    // Computed values
    hasNotifications: notifications.length > 0,
    notificationCount: notifications.length,
    needsRecalibration: recalibrationSuggestions.length > 0
  };
}; 